import {
  COPY_PROJECT_CHILDREN_OVERLAY,
  DELETE_PRODUCT_POSITIONS_OVERLAY
} from "shared/context/overlay/types"
import { noop } from "shared/lib/noop"
import { findParent } from "shared/lib/treeUtils"
import { useEvent } from "shared/lib/useEvent"
import { useOverlay } from "shared/lib/useOverlay"
import {
  BatchCreateableProductPosition,
  ProductPosition
} from "shared/types/ProductPosition"
import { MinimalProject, ProjectTypename } from "shared/types/Project"
import {
  ProjectParent,
  ProjectPermissions
} from "shared/types/__generated__/graphql"

import { useBatchCreateProductPositions } from "entities/product-positions/api/useBatchCreateProductPositions"
import { useProjectData } from "entities/projects/api/useProjectData"
import { ProjectPermissionType } from "entities/projects/types/ProjectPermissions"
import { MinimalRoom } from "entities/rooms/types/Room"

interface UseProductPositionMultiActionHandlersArgs {
  projectID: string
  productPositions: ProductPosition[]
  permissions: ProjectPermissions
  onAction?: () => void
}

export type FilteredProductPositionMultiActionHandlers = {
  onEdit: (() => void) | undefined
  onDuplicate: (() => void) | undefined
  onCopy: (() => void) | undefined
  onDelete: (() => void) | undefined
}

export function useProductPositionMultiActionHandlers({
  projectID,
  productPositions,
  permissions,
  onAction
}: UseProductPositionMultiActionHandlersArgs): FilteredProductPositionMultiActionHandlers {
  const { project } = useProjectData(projectID, true)
  const { openOverlay: openCopyProjectChildrenOverlay } = useOverlay(
    COPY_PROJECT_CHILDREN_OVERLAY
  )
  const { openOverlay: deleteProductPositionsOverlay } = useOverlay(
    DELETE_PRODUCT_POSITIONS_OVERLAY
  )
  const [batchCreateProductPositions] = useBatchCreateProductPositions()

  const handleDuplicate = useEvent(() => {
    const variantsByParent = new Map<string, ProductPosition[]>()

    productPositions.forEach(productPosition => {
      const parent = (findParent(productPosition.id, project?.children) ||
        project) as ProjectParent
      const parentId = parent.id

      if (!variantsByParent.has(parentId)) {
        variantsByParent.set(parentId, [])
      }
      const parentVariants = variantsByParent.get(parentId)
      if (parentVariants) {
        parentVariants.push(productPosition)
      }
    })

    const productPositionsWithParents: BatchCreateableProductPosition[] = []

    variantsByParent.forEach(variants => {
      const sortedVariants = variants.sort((a, b) => a.position - b.position)

      sortedVariants.forEach(productPosition => {
        const parent = (findParent(productPosition.id, project?.children) ||
          project) as MinimalRoom | MinimalProject

        const lowerPositionDuplicates = sortedVariants.filter(
          v => v.position < productPosition.position
        ).length

        productPositionsWithParents.push({
          ...productPosition,
          parent: {
            id: parent.id,
            type: parent?.__typename || ProjectTypename
          },
          position: productPosition.position + 1 + lowerPositionDuplicates
        })
      })
    })

    onAction?.()

    return batchCreateProductPositions({
      productPositions: productPositionsWithParents,
      projectID
    })
  })

  const handleCopy = useEvent(() => {
    openCopyProjectChildrenOverlay({
      projectChildren: productPositions,
      projectID
    })
  })

  const handleDelete = useEvent(() => {
    deleteProductPositionsOverlay({ projectID, productPositions })
    onAction?.()
  })

  const canEdit = permissions[ProjectPermissionType.EDIT]

  return {
    onEdit: canEdit ? noop : undefined,
    onDuplicate: canEdit ? handleDuplicate : undefined,
    onCopy: canEdit ? handleCopy : undefined,
    onDelete: canEdit ? handleDelete : undefined
  }
}
